import * as flux from 'flux';
import GlobalDispatcher from '../dispatcher/Dispatcher';
import { guid } from 'utilities/guid';
import { ActionTypes } from './BaseActionTypes';

export { ResponseHandler } from './ResponseHandler';

const LOADER_TIMEOUT = 1000;

export class BaseActions {

    constructor(private localDispatcher?: flux.Dispatcher<unknown>) {

    }

    private globalDispatch(payload: { type: string, data?: any }, cb?: () => void) {

        if (cb) {
            payload['$callback'] = cb;
        }

        setTimeout(() => {
            GlobalDispatcher.dispatch(payload);
        }, 0);
    }

    /**
     * Returns App store state
     * @param options: IAlertDialogOptions
     */
    get appState(): IAppStoreState {
        return AppStore.getState();
    }

    /**
     * Show alert dialog
     * @param options: IAlertDialogOptions
     */
    alert(options: IAlertDialogOptions) {
        this.globalDispatch({
            type: ActionTypes.Alert,
            data: options
        });
    }

    closeToast(toast: IToast) {
        this.globalDispatch({
            type: ActionTypes.CloseToast,
            data: toast
        });
    }

    /**
     * Show confirmation dialog
     * @param options: IConfirmDialogOptions 
     */
    confirm(options: IConfirmDialogOptions) {
        options.confirm = options.confirm !== undefined ? options.confirm : 'Yes';
        options.decline = options.decline !== undefined ? options.decline : 'No';

        this.globalDispatch({
            type: ActionTypes.Confirm,
            data: options
        });
    }

    /**
     * Show dialog
     * @param options: IDialogOptions
     */
    dialog(options: IDialogOptions) {
        this.globalDispatch({
            type: ActionTypes.Dialog,
            data: options
        });
    }

    /**
     * Dispatch to Store
     * @param payload: { type: string, data?: any }
     */
    dispatch = (payload: { type: string, data?: any }, cb?: () => void) => {
        if (cb) {
            payload['$callback'] = cb;
        }

        setTimeout(() => {
            (this.localDispatcher || GlobalDispatcher).dispatch(payload);
        }, 0);
    }

    /**
     * Show loader after timeout expires
     * @param [timer=LOADER_TIMEOUT] - number of millseconds to wait before displaying loader
     * @returns { stop: ()=> void }
     * @example
     * const loader = this.start();
     * ...ajax call
     * loader.stop();
     */
    loading = (timeout: number = LOADER_TIMEOUT, cb?: () => void) => {
        const timer = setTimeout(() => {
            this.loader(true, cb);
        }, LOADER_TIMEOUT);

        return {
            stop: () => {
                clearTimeout(timer);
                this.loader(false);
            }
        };
    }

    /**
     * Show loader after timeout expires
     * @param msg: Message to display
     * @param type: AlertType
     * @param duration: optional. milliseconds to show.
     */
    toast = (message: string | JSX.Element, type: AlertType, duration?: number) => {
        if (!message) {
            return;
        }

        duration = (duration || 15) * 1000;

        this.globalDispatch({
            type: ActionTypes.Toast,
            data: {
                id: guid(),
                message: message,
                type: type,
                duration: duration
            } as IToast
        });
    }

    /**
     * Transition to route
     * @param url: string
     */
    transitionTo = (path: string) => {
        this.globalDispatch({
            type: ActionTypes.TransitionTo,
            data: path
        });
    }

    getNode = (itemId: string) => {
        return AppStore.getNode(itemId);
    }

    unselectNode = (itemId: string) => {
        this.globalDispatch({
            type: ActionTypes.UnselectNode,
            data: itemId  
        });
    }

    unselectNodes = (itemIds: string[]) => {
        this.globalDispatch({
            type: ActionTypes.UnselectNode,
            data: itemIds
        });
    }

    expandNode = (itemId: string) => {
        this.globalDispatch({
            type: ActionTypes.ExpandNode,
            data: itemId
        });
    }

    private loader(isLoading: boolean, cb?: () => void) {
        const store: IAppStoreState = AppStore.getState();

        if (isLoading && store.loading) {
            return;
        }

        this.globalDispatch({
            type: ActionTypes.Loading,
            data: isLoading
        }, cb);
    }
}
